import { router } from "@inertiajs/react";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DateRangeFilter from "@/components/admin-portal/appointment/date-range-filter";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { Button } from "@/components/ui/button";
import type {
	ExternalProp,
	RoutingHistoryFilters,
	RoutingHistoryOptions,
	RoutingPolicyChange,
} from "@/types/admin-portal/routing-mode";
import { HISTORY_DEFAULT_LIMIT, historyQuery } from "./api";
import {
	type ActiveFilter,
	FilterPanel,
	FilterToggle,
	useFilterPanel,
} from "./filter-panel";
import { HistoryTimeline } from "./history-timeline";
import { LoadError } from "./load-error";
import { SearchCombobox, toOptions } from "./search-combobox";

// from/to are YYYY-MM-DD days, as the backend reads them.
function formatRange(
	from: string | undefined,
	to: string | undefined,
	t: TFunction,
) {
	const day = (d: string) =>
		new Date(`${d}T00:00:00`).toLocaleDateString("id-ID", {
			dateStyle: "medium",
		});
	if (from && to) return `${day(from)} – ${day(to)}`;
	if (from) return t("history.filters.since", { date: day(from) });
	return t("history.filters.until", { date: day(to ?? "") });
}

interface HistoryPanelProps {
	changes?: ExternalProp<RoutingPolicyChange[]>;
	filters: RoutingHistoryFilters;
	options?: RoutingHistoryOptions;
}

export function HistoryPanel({ changes, filters, options }: HistoryPanelProps) {
	const { t } = useTranslation("routing-mode");
	const [draft, setDraft] = useState<RoutingHistoryFilters>(filters);
	const panel = useFilterPanel("routing-mode-history-preferences");
	const locations = options?.locations ?? [];
	// The backend filters by one location only, so a province just narrows the city
	// list; it opens on the province of the city already filtered on.
	const [province, setProvince] = useState(
		() => locations.find((l) => l.id === filters.locationId)?.state ?? "",
	);
	const limit = changes?.meta?.limit ?? HISTORY_DEFAULT_LIMIT;

	function load(next: RoutingHistoryFilters, page = 1, pageSize = limit) {
		router.get(window.location.pathname, historyQuery(next, page, pageSize), {
			only: ["changes", "historyFilters"],
			preserveState: true,
			preserveScroll: true,
			replace: true,
		});
	}

	// Fields apply as they change, like the Orders page.
	function change(patch: RoutingHistoryFilters) {
		const next = { ...draft, ...patch };
		setDraft(next);
		load(next);
	}

	const provinceOptions = toOptions(locations.map((l) => l.state));
	const cityOptions = locations
		.filter((l) => !province || l.state === province)
		.map((l) => ({ value: String(l.id), label: l.city }));

	function pickProvince(next: string) {
		setProvince(next);
		// A city from another province would contradict the pick.
		const city = locations.find((l) => l.id === draft.locationId);
		if (next && city && city.state !== next) change({ locationId: undefined });
	}
	const actorOptions = (options?.admins ?? []).map((a) => ({
		value: a.userId,
		label: a.email ? `${a.name} (${a.email})` : a.name,
	}));

	const activeFilters: ActiveFilter[] = [
		filters.locationId && {
			key: "location",
			label: t("locations.filter.city"),
			value:
				locations.find((l) => l.id === filters.locationId)?.city ??
				String(filters.locationId),
			onClear: () => change({ locationId: undefined }),
		},
		filters.changedBy && {
			key: "actor",
			label: t("history.filters.actor"),
			value:
				options?.admins.find((a) => a.userId === filters.changedBy)?.name ??
				filters.changedBy,
			onClear: () => change({ changedBy: undefined }),
		},
		(filters.from || filters.to) && {
			key: "date",
			label: t("history.filters.date"),
			value: formatRange(filters.from, filters.to, t),
			onClear: () => change({ from: undefined, to: undefined }),
		},
	].filter((f): f is ActiveFilter => Boolean(f));

	const filterBar = (
		<>
			<div>
				<FilterToggle
					open={panel.open}
					activeCount={activeFilters.length}
					onToggle={panel.toggle}
				/>
			</div>

			<FilterPanel
				open={panel.open}
				onClose={panel.toggle}
				onClearAll={() => {
					setDraft({});
					setProvince("");
					load({});
				}}
				activeFilters={activeFilters}
			>
				<SearchCombobox
					options={provinceOptions}
					value={province}
					onChange={pickProvince}
					allLabel={t("locations.filter.all_states")}
					searchPlaceholder={t("locations.filter.search_state")}
					emptyText={t("locations.filter.no_match")}
					ariaLabel={t("locations.filter.state")}
				/>
				<SearchCombobox
					options={cityOptions}
					value={draft.locationId ? String(draft.locationId) : ""}
					onChange={(v) => change({ locationId: v ? Number(v) : undefined })}
					allLabel={t("locations.filter.all_cities")}
					searchPlaceholder={t("locations.filter.search_city")}
					emptyText={t("locations.filter.no_match")}
					ariaLabel={t("locations.filter.city")}
				/>
				<div className="col-span-full md:col-span-2">
					<DateRangeFilter
						value={{ from: draft.from ?? "", to: draft.to ?? "" }}
						onChange={(next) =>
							change({ from: next.from || undefined, to: next.to || undefined })
						}
						placeholder={t("history.filters.date_placeholder")}
					/>
				</div>
				<SearchCombobox
					options={actorOptions}
					value={draft.changedBy ?? ""}
					onChange={(v) => change({ changedBy: v || undefined })}
					allLabel={t("history.filters.all_actors")}
					searchPlaceholder={t("history.filters.search_actor")}
					emptyText={t("locations.filter.no_match")}
					ariaLabel={t("history.filters.actor")}
				/>
			</FilterPanel>
		</>
	);

	if (changes?.error || !changes?.data) {
		return (
			<div className="flex flex-col gap-4">
				{filterBar}
				<LoadError
					error={changes?.error ?? t("failed.body")}
					only={["changes"]}
				/>
			</div>
		);
	}

	const rows = changes.data;
	const page = changes.meta?.page ?? 1;
	const from = (page - 1) * limit + 1;

	return (
		<div className="flex flex-col gap-4">
			{filterBar}

			{rows.length === 0 ? (
				<div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
					{t("history.empty")}
					{/* A page past the end (an old link, or paging while a filter loaded)
					    has no pager to leave by. */}
					{page > 1 && (
						<Button variant="outline" size="sm" onClick={() => load(draft)}>
							{t("history.first_page")}
						</Button>
					)}
				</div>
			) : (
				<HistoryTimeline rows={rows} locations={locations} />
			)}

			{rows.length > 0 && (
				<ApptPagination
					metadata={{
						page,
						pages: changes.meta?.totalPages ?? 1,
						limit,
						count: changes.meta?.totalItems ?? rows.length,
						from,
						to: from + rows.length - 1,
					}}
					label={t("history.pagination_label")}
					actions={{
						goToPrevpage: () => load(draft, page - 1),
						goToNextPage: () => load(draft, page + 1),
						goToPage: (value) => load(draft, Number(value)),
						goToFirstPage: () => load(draft),
						goToLastPage: () => load(draft, changes.meta?.totalPages ?? 1),
						onChangeLimit: (value) => load(draft, 1, Number(value)),
					}}
				/>
			)}
		</div>
	);
}
